import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XIcon } from 'lucide-react';
import { logAccess, logAccessGranted } from '../utils/accessLog';
interface AccessModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAuthenticated: () => void;
  onRequestAccess: (name: string, email: string) => void;
}
export function AccessModal({
  isOpen,
  onClose,
  onAuthenticated,
  onRequestAccess
}: AccessModalProps) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    // Password verification — stored obfuscated, decoded at runtime
    const _k = atob('RG9mUDIwMjY=');
    if (password === _k) {
      localStorage.setItem('dop_authenticated', 'true');
      // Send access data to Google Sheets via webhook
      logAccessGranted(name, email);
      onAuthenticated();
    } else {
      setError('Incorrect password. Please try again or request access.');
    }
  };
  const handleRequestAccessClick = () => {
    onRequestAccess(name, email);
  };
  return <AnimatePresence data-id="element-6">
      {isOpen && <div className="fixed inset-0 z-50 flex items-center justify-center px-4" data-id="element-7">
          {/* Backdrop */}
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.6
      }} className="absolute inset-0 bg-obsidian/80 backdrop-blur-md" onClick={onClose} data-id="element-8" />

          {/* Modal Card */}
          <motion.div initial={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.95,
        y: 10
      }} transition={{
        duration: 0.6,
        ease: [0.16, 1, 0.3, 1]
      }} className="relative w-full max-w-md bg-charcoal border border-borderline p-8 md:p-10 shadow-2xl z-10" data-id="element-9">
            <button onClick={onClose} className="absolute top-6 right-6 text-muted hover:text-offwhite transition-colors" aria-label="Close modal" data-id="element-10">
              <XIcon className="w-5 h-5" data-id="element-11" />
            </button>

            <div className="mb-8" data-id="element-12">
              <h2 className="text-3xl mb-3 text-offwhite tracking-wide" data-id="element-13">
                Private Project Access
              </h2>
              <p className="text-muted text-sm font-light leading-relaxed" data-id="element-14">
                Enter your email and the access password to view full project
                information
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-5" data-id="element-15">
              <div data-id="element-16">
                <label htmlFor="name" className="block text-xs uppercase tracking-widest text-muted mb-2" data-id="element-17">
                  Name
                </label>
                <input id="name" type="text" required value={name} onChange={e => setName(e.target.value)} className="w-full bg-obsidian border border-borderline px-4 py-3 text-offwhite focus:outline-none focus:border-muted transition-colors font-light" data-id="element-18" />
              </div>

              <div data-id="element-19">
                <label htmlFor="email" className="block text-xs uppercase tracking-widest text-muted mb-2" data-id="element-20">
                  Email
                </label>
                <input id="email" type="email" required value={email} onChange={e => setEmail(e.target.value)} className="w-full bg-obsidian border border-borderline px-4 py-3 text-offwhite focus:outline-none focus:border-muted transition-colors font-light" data-id="element-21" />
              </div>

              <div data-id="element-22">
                <label htmlFor="password" className="block text-xs uppercase tracking-widest text-muted mb-2" data-id="element-23">
                  Access Password
                </label>
                <input id="password" type="password" required value={password} onChange={e => setPassword(e.target.value)} className="w-full bg-obsidian border border-borderline px-4 py-3 text-offwhite focus:outline-none focus:border-muted transition-colors font-light" data-id="element-24" />
              </div>

              {error && <motion.p initial={{
            opacity: 0,
            y: -5
          }} animate={{
            opacity: 1,
            y: 0
          }} className="text-red-400/80 text-sm font-light" data-id="element-25">
                  {error}
                </motion.p>}

              <div className="pt-2" data-id="element-26">
                <button type="submit" className="w-full bg-offwhite text-obsidian py-4 text-sm uppercase tracking-widest font-medium hover:bg-white transition-colors duration-500 box-glow-hover mb-6" data-id="element-27">
                  Access Now →
                </button>

                <div className="text-center" data-id="element-28">
                  <button type="button" onClick={handleRequestAccessClick} className="text-muted text-sm font-light hover:text-offwhite hover:underline underline-offset-4 transition-all duration-300" data-id="element-29">
                    I don't have a password — Request Access
                  </button>
                </div>
              </div>
            </form>
          </motion.div>
        </div>}
    </AnimatePresence>;
}